/* eslint-disable @typescript-eslint/no-explicit-any */
import { combineReducers, PayloadAction, Reducer, Store } from '@reduxjs/toolkit';
import {
  actionChannel,
  call,
  cancel,
  fork,
  put,
  spawn,
  take,
} from 'redux-saga/effects';
import { buffers, Channel, SagaMiddleware, Task } from 'redux-saga';

/**
 *  wrap an action with meta data for logging where the action was dispatched
 * @param action - action to be dispatched
 * @param initiator - the source of the action
 * @param shouldUseMetaInfo - if true, the action will be dispatched  with meta info
 * @param hint - additional information to be logged
 * @returns - the action with meta data
 * @example
 * dispatch(fetchSiteSubjectsList()); // { type: 'fetchSiteSubjectsList', payload: { ... }, meta: { initiator: 'component', fullFilePath: 'src/components/SubjectsList.tsx?t=122356;22:4' } }
 * dispatch(wrapLogAction(fetchSiteSubjectsList(), 'component'));
 * yield put(wrapLogAction(fetchSiteSubjectsList(), 'saga'));
 */
export function wrapLogAction(
  action: PayloadAction<any>,
  initiator: string,
  shouldUseMetaInfo: boolean,
  hint?: string
) {
  if (!shouldUseMetaInfo) {
    return action;
  }
  try {
    const e = new Error();
    const fullFilePath = e.stack?.split('\n')[3].trim();
    (action as any).meta = { initiator, fullFilePath, hint };

    return action;
  } catch {
    return action;
  }
}

export const logError = <T>(error: T) => console.error(error);

export const makeSagaDispatch =
  (shouldUseMetaInfo: boolean) => (action: PayloadAction<any>, hint?: string) => {
    const act = wrapLogAction(action, 'saga', shouldUseMetaInfo, hint);
    return put(act);
  };

const isLocalBrowser =
  typeof window !== 'undefined' && window.location.host.includes('localhost');
const isJest = typeof process !== 'undefined' && process.env.NODE_ENV === 'jest';

export const dispatch = makeSagaDispatch(isLocalBrowser && !isJest);

export function createSagaInjector(
  runSaga: SagaMiddleware<object>['run'],
  rootSaga: () => Generator
) {
  // Create a dictionary to keep track of injected sagas
  const injectedSagas = new Map<string, Task>();

  const isInjected = (key: string) => injectedSagas.has(key);

  const injectSaga = (key: string, saga: () => Generator) => {
    // We won't run saga if it is already injected
    if (isInjected(key)) {
      return;
    }

    // Sagas return task when they executed, which can be used
    // to cancel them
    const task = runSaga(saga);

    /**
     * Name a dead feature saga (D7-7639). Sagas run unsupervised here, so an
     * uncaught throw in any child kills the tree permanently and every button
     * it served silently stops working until reload. This cannot revive it —
     * the children are already cancelled — but redux-saga's own reporter names
     * only the failing task, not the feature that died with it.
     */
    task.toPromise().catch((error: unknown) => {
      console.error(
        `Saga "${key}" terminated and will NOT restart — every watcher it ` +
          'registered is dead until the page reloads.',
        error
      );
    });

    // Save the task if we want to cancel it in the future
    injectedSagas.set(key, task);
  };

  const injectSagas = (sagas: Record<string, () => Generator>) => {
    // We won't run saga if it is already injected
    Object.entries(sagas).forEach(([key, saga]) => {
      if (!isInjected(key)) {
        injectSaga(key, saga);
      }
    });
  };

  // Inject the root saga as it a statically loaded file,
  injectSaga('root', rootSaga);

  const cancelSaga = (key: string) => {
    const task = injectedSagas.get(key);
    if (task?.isRunning()) {
      task.cancel();
    }
    injectedSagas.delete(key);
  };

  const cancelSagas = (keys: string[]) => {
    keys.forEach(key => {
      cancelSaga(key);
    });
  };

  const getInjectedSagas = () => {
    return Object.fromEntries(injectedSagas.entries());
  };

  return {
    injectSaga,
    cancelSaga,
    injectSagas,
    cancelSagas,
    getInjectedSagas,
  };
}

export function createReducerInjector(
  replaceReducer: Store['replaceReducer'],
  rootReducer: Record<string, Reducer>
) {
  // Create a dictionary to keep track of injected reducers
  const injectedReducers = new Map<string, Reducer>();
  const injectReducer = (key: string, red: Reducer) => {
    // We won't inject reducer if it is already injected

    if (injectedReducers.has(key)) {
      return;
    }

    const result = { ...rootReducer, ...injectedReducers, [key]: red };

    const combined = combineReducers(result) as Reducer;

    replaceReducer(combined);

    injectedReducers.set(key, red);
  };
  const injectReducers = (reducers: Record<string, Reducer>) => {
    const reducersNames = Object.keys(reducers).filter(key => !injectedReducers.has(key));
    if (reducersNames.length === 0) {
      return;
    }

    const reducersToInject = reducersNames.reduce(
      (acc, key) => {
        acc[key] = reducers[key];
        injectedReducers.set(key, reducers[key]);
        return acc;
      },
      {} as Record<string, Reducer>
    );

    const result = {
      ...rootReducer,
      ...Object.fromEntries(injectedReducers.entries()),
      ...reducersToInject,
    };

    const combined = combineReducers(result) as Reducer;

    replaceReducer(combined);
  };

  const removeReducer = (key: string) => {
    injectedReducers.delete(key);
    const restInjected = Object.fromEntries(injectedReducers.entries());
    const combined = combineReducers({
      ...rootReducer,
      ...restInjected,
    }) as Reducer;

    replaceReducer(combined);
  };
  const removeReducers = (keys: string[]) => {
    keys.forEach(key => injectedReducers.delete(key));
    const restInjected = Object.fromEntries(injectedReducers.entries());
    const combined = combineReducers({
      ...rootReducer,
      ...restInjected,
    }) as Reducer;

    replaceReducer(combined);
  };

  const getInjectedReducers = () => {
    return Object.fromEntries(injectedReducers.entries());
  };

  return {
    injectReducer,
    injectReducers,
    removeReducer,
    removeReducers,
    getInjectedReducers,
  };
}

export function getURLParams(route: string): Record<string, string> {
  const routeParts = route.split('/');
  let pathname = window.location.pathname;
  const publicUrl = (window as any).PUBLIC_URL;

  if (publicUrl) {
    pathname = pathname.replace(publicUrl, '/');
  }

  const pathParts = pathname.split('/');

  const params: Record<string, string> = {};

  if (routeParts.length !== pathParts.length) {
    throw new Error('Route and pathname do not match');
  }

  routeParts.forEach((part, index) => {
    if (part.startsWith(':')) {
      const key = part.slice(1);
      params[key] = pathParts[index];
    }
  });

  return params;
}

export function getParamsFromUrl<T extends Record<string, string> = Record<string, string>>(
  route: string
): T {
  let pathname = window.location.pathname;
  const publicUrl = (window as any).PUBLIC_URL;

  if (publicUrl) {
    pathname = pathname.replace(publicUrl, '/');
  }

  const paramNames = route.match(/:[^/]+/g)?.map(p => p.slice(1)) || [];
  const urlSegments = pathname.split('/').filter(Boolean);
  const routeSegments = route.split('/').filter(Boolean);

  return paramNames.reduce((params, name) => {
    const paramIndex = routeSegments.findIndex(s => s === `:${name}`);
    return {
      ...params,
      [name]: urlSegments[paramIndex] || '',
    };
  }, {} as T);
}

// Global state storage for makeRestartable to persist across generator restarts

export function makeRestartable(saga: () => Generator) {
  const restartableStateMap = new Map<symbol, { restartCount: number; lastRestartTime: number }>();
  const maxRestarts = 5;
  const restartWindowMs = 1000; // 1 second

  // Create unique symbol for this makeRestartable instance
  const sagaKey = Symbol('makeRestartable');

  // Initialize state for this generator function
  restartableStateMap.set(sagaKey, { restartCount: 0, lastRestartTime: 0 });

  return function* () {
    yield spawn(function* () {
      const state = restartableStateMap.get(sagaKey)!;

      while (true) {
        try {
          yield call(saga);
          console.error(
            'unexpected root saga termination. The root sagas are supposed to be sagas that live during the whole app lifetime!',
            saga
          );
          // Reset restart count on successful execution
          state.restartCount = 0;
          state.lastRestartTime = 0;
        } catch (e) {
          const now = Date.now();
          const timeSinceLastRestart =
            state.lastRestartTime > 0 ? now - state.lastRestartTime : Infinity;

          // Reset count if more than restartWindowMs passed since last restart
          if (state.lastRestartTime > 0 && timeSinceLastRestart > restartWindowMs) {
            state.restartCount = 0;
          }

          // Update lastRestartTime AFTER calculating timeSinceLastRestart
          state.lastRestartTime = now;
          state.restartCount++;

          console.error(
            `Saga error, restarting (${state.restartCount}/${maxRestarts}). Time since last restart: ${timeSinceLastRestart}ms`,
            e
          );

          if (state.restartCount > maxRestarts) {
            console.error(
              `Saga crashed ${maxRestarts} times within ${restartWindowMs}ms, stopping execution:`,
              e
            );
            return;
          }
        }
      }
    });
  };
}

type QueueAction = {
  type: string;
};

type QueueExecutor<A extends QueueAction> = (action: A) => Generator;

type TakeQueueOptions<A extends QueueAction> = {
  getChannelId: (action: A) => string;
  bufferSize?: number;
};

/**
 * Routes matching Redux actions to a FIFO channel selected by `getChannelId`.
 * Actions with the same id are consumed sequentially by one executor; actions
 * with different ids are handled by independent executors.
 *
 * The helper owns channel consumption and invokes the executor once per action.
 * Requiring an explicit id selector prevents different action types for the
 * same entity from silently ending up in separate queues.
 */
function* consumeQueue(channel: Channel<any>, executor: QueueExecutor<any>) {
  while (true) {
    const action: QueueAction = yield take(channel);
    yield call(executor, action);
  }
}

export function takeQueue<A extends QueueAction>(
  actionTypes: string[],
  executor: QueueExecutor<A>,
  options: TakeQueueOptions<A>
): Generator;
export function takeQueue(
  actionTypes: string[],
  executor: QueueExecutor<any>,
  options: TakeQueueOptions<any>
): Generator;
export function* takeQueue<A extends QueueAction>(
  actionTypes: string[],
  executor: QueueExecutor<A>,
  { getChannelId, bufferSize = 10 }: TakeQueueOptions<A>
) {
  const channels = new Map<string, Channel<A>>();
  const workers: Task[] = [];

  try {
    while (true) {
      const action: A = yield take(actionTypes);

      const channelId = getChannelId(action);

      let channel = channels.get(channelId);

      if (!channel) {
        channel = (yield actionChannel(
          (candidate: QueueAction) =>
            actionTypes.includes(candidate.type) &&
            getChannelId(candidate as A) === channelId,
          buffers.expanding(bufferSize)
        )) as Channel<A>;

        channels.set(channelId, channel);

        workers.push(yield fork(consumeQueue, channel, executor));
        // Seed the channel: the outer `take` already consumed this action and
        // `actionChannel` only captures actions dispatched after its creation.
        yield put(channel, action);
      }
    }
  } finally {
    // Closing a channel ends an executor blocked on take(channel); cancellation
    // also runs each executor's finally block.
    channels.forEach(channel => channel.close());
    if (workers.length) {
      yield cancel(workers);
    }
  }
}
